import { AgencyPage } from '../../../components/Views/AgencyPage';
import { Layout } from '../../../components/Layout/Layout';
import initApollo from '../../../utils/init-Apollo';
import { getStaticData } from '../../../utils/helpers/getStaticData';
import { getAgencySeoData } from '../../../utils/helpers/seo';
import { getSelectedAgencyQuery } from '../../../api/queries';
import { normalizePath } from '../../../utils/helpers';
import { withBugsnagGetServerSideProps } from '../../../utils/withBugsnag';
import { loadAgencyMockData } from '../../../utils/helpers/loadAgencyMockData';
import { extractApolloState } from '../../../utils/helpers/extractApolloState';
import { getLocaleOnSSR } from '../../../utils/helpers/getLocaleOnSSR';
import { getSSRContextData } from '../../../utils/helpers/ssrContext';
import { shouldUseMock } from '../../../utils/helpers/shouldUseMock';
import {
  parseAgencySegments,
  isValidAgencyUrl,
  getCategorySlug,
  getCanonicalAgencyPath,
  getEmptyResultsRedirectDestination
} from '../../../utils/helpers/agencyPage';
import { AgencyFilterDealTypeEnum } from '../../../utils/constants/agencyPage';
import { Language, NextPageWithLayout } from '../../../utils/types';

type AgencyProps = {
  seoData: ReturnType<typeof getAgencySeoData> | null;
  agencySlug: string;
  hasError?: boolean;
  currentLang: Language;
  initialFilter?: {
    dealType: AgencyFilterDealTypeEnum | null;
    category: string | null;
  };
};

const Agency: NextPageWithLayout<AgencyProps> = ({
  agencySlug,
  hasError,
  currentLang,
  initialFilter
}) => (
  <AgencyPage
    agencySlug={agencySlug}
    hasError={hasError}
    currentLang={currentLang}
    initialFilter={initialFilter}
  />
);

export const getServerSideProps = withBugsnagGetServerSideProps<AgencyProps>(
  async ({ res, req, params, query }) => {
    const lang = getLocaleOnSSR(req);
    const { platform } = getSSRContextData(req);

    const { agency: rawSlug, segments: rawSegments } = params ?? {};
    const slug = rawSlug as string;
    const segments = Array.isArray(rawSegments) ? rawSegments : [];

    const asPath = `/agentlikler/${slug}${
      segments.length ? `/${segments.join('/')}` : ''
    }`;
    const newAsPath = normalizePath(asPath);

    const apolloClient = initApollo({}, { lang, platform });

    if (shouldUseMock(query)) {
      try {
        const agency = await loadAgencyMockData(apolloClient, slug);
        await getStaticData(apolloClient);

        return extractApolloState(apolloClient, {
          props: {
            seoData: getAgencySeoData(
              agency?.name,
              lang,
              newAsPath,
              segments,
              req
            ),
            agencySlug: slug,
            currentLang: lang
          }
        });
      } catch (error) {
        console.error('Error loading mock data:', error);
      }
    }

    res.setHeader(
      'Cache-Control',
      'public, max-age=60, s-maxage=60, stale-while-revalidate=86400'
    );

    try {
      const [agencyRes] = await Promise.all([
        apolloClient.query({
          query: getSelectedAgencyQuery,
          variables: { slug },
          errorPolicy: 'all'
        }),
        getStaticData(apolloClient)
      ]);

      const agency = agencyRes?.data?.agency ?? null;
      const hasGraphQLErrors = Boolean(agencyRes?.errors?.length);

      if (!agency && hasGraphQLErrors) {
        return {
          props: {
            seoData: null,
            agencySlug: slug,
            hasError: true,
            currentLang: lang
          }
        };
      }

      if (!agency) {
        return { notFound: true };
      }

      const { dealType, category } = parseAgencySegments(
        segments,
        agency.itemCategories
      );

      if (!isValidAgencyUrl(segments, category)) {
        return {
          redirect: { destination: `/agentlikler/${slug}`, permanent: false }
        };
      }

      const categorySlug = getCategorySlug(category, agency.itemCategories);

      const canonicalRedirect = getCanonicalAgencyPath(
        slug,
        segments,
        dealType,
        categorySlug
      );

      if (canonicalRedirect) {
        return {
          redirect: { destination: canonicalRedirect, permanent: false }
        };
      }

      if (segments.length) {
        const emptyRedirect = await getEmptyResultsRedirectDestination({
          apolloClient,
          agencyId: agency.id,
          dealType,
          category,
          categorySlug,
          slug,
          itemCategories: agency.itemCategories
        });

        if (emptyRedirect) {
          return {
            redirect: { destination: emptyRedirect, permanent: false }
          };
        }
      }

      return extractApolloState(apolloClient, {
        props: {
          seoData: getAgencySeoData(
            agency?.name,
            lang,
            newAsPath,
            segments,
            req
          ),
          agencySlug: slug,
          hasError: false,
          currentLang: lang,
          initialFilter: {
            dealType,
            category
          }
        }
      });
    } catch (error) {
      res.setHeader('Cache-Control', 'no-store');
      throw error;
    }
  },
  'agencies/[agency]/[[...segments]]',
  {
    seoData: null,
    agencySlug: '',
    hasError: true
  }
);

Agency.getLayout = (page, pageProps) => (
  <Layout
    isBottomNavigation={false}
    isBanner={false}
    isShowCloseButton={false}
    pageProps={pageProps}
    hideMobileHeader
  >
    {page}
  </Layout>
);

export default Agency;
